<template>
    <div class="tw:flex tw:flex-col tw:gap-4 tw:pb-4">

        <!-- Error -->
        <div
            v-if="error"
            class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm"
        >
            {{ error }}
        </div>

        <!-- Mapa del recinto -->
        <VenueMapCard
            :svg-markup="svgContent"
            :zones="zonas"
            :selected-zone-id="zonaSeleccionada && zonaSeleccionada.id"
            :hovered-zone-id="tableHoveredId"
            :loading="loading"
            wrapper-class="tw:h-full tw:sm:h-96"
            container-class="tw:h-full"
            :title="$t('perfil_invitaciones.selecciona_zona')"
            :hint="$t('perfil_invitaciones.selecciona_zona_hint')"
            @zone-click="$emit('seleccionar-zona', $event)"
            @zone-colors="onZoneColors"
            @zone-hover="onMapZoneHover"
        />

        <!-- Card: Lista de sectores -->
        <ZoneTableCard
            :zones="zonasFiltradas"
            :loading="loading"
            :title="$t('perfil_invitaciones.listado_sectores')"
            :total-libres="totalLibres"
            :libres-label="$t('perfil_invitaciones.libres')"
            :zone-colors="svgZoneColors"
            :show-availability="true"
            :hovered-zone-id="svgHoveredId"
            :show-search="true"
            :search-value="filtroZona"
            :search-label="$t('perfil_invitaciones.filtrar_sectores')"
            @select="$emit('seleccionar-zona', $event)"
            @zone-hover="onTableZoneHover"
            @update:search="filtroZona = $event"
        />

    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import VenueMapCard from '@/modules/front/ticketing/VenueMapCard.vue';
    import ZoneTableCard from '@/modules/front/ticketing/ZoneTableCard.vue';

    const props = defineProps({
        zonas: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Boolean],
            default: null,
        },
        zonaSeleccionada: {
            type: Object,
            default: null,
        },
        /** Contenido SVG del mapa del recinto (opcional) */
        svgContent: {
            type: String,
            default: '',
        },
    });

    defineEmits(['seleccionar-zona']);

    /** Colores reales extraídos del SVG por VenueMap. { [zoneId]: colorHex } */
    const svgZoneColors = ref({});
    /** Texto introducido por el usuario para filtrar sectores por nombre */
    const filtroZona = ref('');

    // ─── Hover sync mapa ↔ lista ─────────────────────────────────────────────

    /**
     * Zone id hovered on the SVG map — propagated DOWN to ZoneTable so its row
     * gets highlighted and scrolled into view.
     */
    const svgHoveredId = ref(null);

    /**
     * Zone id hovered on the ZoneTable — propagated DOWN to VenueMap so the
     * matching SVG region gets the `highlighted-svg` class.
     */
    const tableHoveredId = ref(null);

    /** Debounce handle — mirrors the legacy hover_timeout pattern. */
    let _hoverTimeout = null;

    function onZoneColors(colors) {
        svgZoneColors.value = colors;
    }

    /**
     * Gestiona el hover sobre zonas del mapa SVG.
     * Dirección: mapa → lista
     *   - Actualiza svgHoveredId (pasado como :hovered-zone-id a ZoneTable)
     *   - El tooltip flotante lo gestiona VenueMap internamente
     *
     * @param {number}     zoneId   - Id de la zona
     * @param {boolean}    entering - true = mouseenter, false = mouseleave
     * @param {MouseEvent} event    - Evento nativo del ratón
     */
    function onMapZoneHover(zoneId, entering) {
        clearTimeout(_hoverTimeout);

        if (entering) {
            // Highlight con debounce de 50ms (igual que el legacy hover_timeout)
            _hoverTimeout = setTimeout(() => {
                svgHoveredId.value = zoneId;
            }, 50);
        } else {
            _hoverTimeout = setTimeout(() => {
                svgHoveredId.value = null;
            }, 0);
        }
    }

    /**
     * Gestiona el hover sobre filas de la ZoneTable.
     * Dirección: lista → mapa
     *
     * @param {number|string} zoneId
     * @param {boolean}       entering
     */
    function onTableZoneHover(zoneId, entering) {
        clearTimeout(_hoverTimeout);
        _hoverTimeout = setTimeout(() => {
            tableHoveredId.value = entering ? zoneId : null;
        }, 50);
    }

    /** Zonas filtradas: solo disponibles, por nombre */
    const zonasFiltradas = computed(() => {
        const disponibles = props.zonas.filter(z => z.available);
        const q = filtroZona.value.trim().toLowerCase();
        if (!q) return disponibles;
        return disponibles.filter(z => z.name?.toLowerCase().includes(q));
    });

    /** Total de plazas libres entre todas las zonas disponibles */
    const totalLibres = computed(() => {
        const zonesWithAvailability = props.zonas.filter(
            z => z.available && z.availability != null
        );
        if (!zonesWithAvailability.length) return null;
        return zonesWithAvailability.reduce((acc, z) => acc + (z.availability || 0), 0);
    });
</script>
